// src/loaders/objLoader.js
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js'
import { MTLLoader } from 'three/examples/jsm/loaders/MTLLoader.js'
import * as THREE from 'three'
import {
  createStandardMaterial,
  createMesh,
  mergeAndNormalizeGeometry,
  centerAndOrientGeometry,
  setupColorAttribute,
  setupWeightAttribute,
  bakeTextureToVertexColors,
} from './loaderUtils'

/**
 * Load OBJ model from a path, Blob URL, or file data object
 * @param {string|Object} pathOrData - File path, Blob URL, or object with { objUrl, mtlUrl, textureUrls }
 * @returns {Promise<THREE.Mesh>}
 */
export function loadOBJ(pathOrData) {
  return new Promise((resolve, reject) => {
    let isFileData = false;
    let objPath = pathOrData;
    let mtlPath = null;
    let textureMap = null;
    let textureManager = null;

    // Check if this is file data from uploaded files
    if (typeof pathOrData === 'object') {
      isFileData = true;
      objPath = pathOrData.objUrl;
      mtlPath = pathOrData.mtlUrl;

      // Create a map of texture names to Blob URLs for easy lookup
      textureMap = {};
      if (pathOrData.textureUrls) {
        pathOrData.textureUrls.forEach(tex => {
          textureMap[tex.name] = tex.url;
        });
      }

      // If we have textures, create a custom LoadingManager upfront
      if (Object.keys(textureMap).length > 0) {
        textureManager = new THREE.LoadingManager();

        // Intercept texture loading and map texture names to Blob URLs
        textureManager.setURLModifier((url) => {
          // Extract filename from URL
          const filename = url.split('/').pop();

          // Check if we have a Blob URL for this texture
          if (textureMap[filename]) {
            console.log(`Texture mapper: ${filename} -> Blob URL`);
            return textureMap[filename];
          }

          // Also check by exact match
          if (textureMap[url]) {
            return textureMap[url];
          }

          console.log(`Texture mapper: ${filename} not found in map, using original URL`);
          return url;
        });
      }
    } else {
      // Original behavior for file paths
      const basePath = pathOrData.substring(0, pathOrData.lastIndexOf('/') + 1)
      mtlPath = pathOrData.replace('.obj', '.mtl')
    }

    // Try to load MTL first
    const loadMTL = new Promise((resolveMTL) => {
      if (!mtlPath) {
        resolveMTL(null);
        return;
      }

      const mtlLoader = textureManager
        ? new MTLLoader(textureManager)
        : new MTLLoader();

      if (!isFileData || !textureManager) {
        // Original behavior for file paths
        const basePath = objPath.substring(0, objPath.lastIndexOf('/') + 1)
        mtlLoader.setPath(basePath)
      }

      mtlLoader.load(
        mtlPath,
        (materials) => {
          materials.preload();
          resolveMTL(materials);
        },
        undefined,
        () => resolveMTL(null) // MTL not found, continue without it
      );
    })

    loadMTL.then((materials) => {
      const objLoader = new OBJLoader()

      if (materials) {
        objLoader.setMaterials(materials)
      }

      objLoader.load(
        objPath,
        (root) => {
          // find the first mesh
          let mesh = null
          let originalMaterial = null
          root.traverse((child) => {
            if (child.isMesh && !mesh) {
              mesh = child
              originalMaterial = child.material
            }
          })
          if (!mesh) return reject(new Error('No mesh in OBJ'))

          let geom = mesh.geometry

          // Center and orient geometry
          centerAndOrientGeometry(geom)

          // Merge vertices and compute normals
          // **CRITICAL**: mergeVertices() returns a new indexed geometry object
          // We must capture the return value to ensure proper indexing across all loaders
          geom = mergeAndNormalizeGeometry(geom, 1e-4, 'OBJ')

          // **CRITICAL**: Bake texture AFTER merging to ensure color attribute
          // matches the merged vertex count. UV coordinates are preserved through merging.
          let colorAttribute = null
          const hasUV = geom.hasAttribute('uv')
          if (hasUV && originalMaterial && originalMaterial.map) {
            const texture = originalMaterial.map
            if (texture.image && texture.image.complete) {
              colorAttribute = bakeTextureToVertexColors(geom, texture)
            }
          }

          // Setup color attribute with baked texture if available
          if (colorAttribute) {
            geom.setAttribute('color', colorAttribute)
          } else {
            setupColorAttribute(geom)
          }

          // Setup weight attribute
          setupWeightAttribute(geom)

          // Create mesh with standard material
          const finalMesh = createMesh(geom, 'OBJMesh')
          resolve(finalMesh)
        },
        undefined,
        reject
      )
    })
  })
}
